import React, { useCallback, useEffect, useMemo, useState } from 'react';
// eslint-disable-next-line no-restricted-imports
import { useCopyToClipboard } from 'react-use';
import { Color, Spacing } from '@signozhq/design-tokens';
import { Button } from '@signozhq/ui/button';
import { Drawer, Tooltip } from 'antd';
import { ToggleGroupSimple } from '@signozhq/ui/toggle-group';
import { Divider } from '@signozhq/ui/divider';
import { Typography } from '@signozhq/ui/typography';
import cx from 'classnames';
import LogStateIndicator, {
	LogType,
} from 'components/Logs/LogStateIndicator/LogStateIndicator';
import QuerySearch from 'components/QueryBuilderV2/QueryV2/QuerySearch/QuerySearch';
import { convertExpressionToFilters } from 'components/QueryBuilderV2/utils';
import { FeatureKeys } from 'constants/features';
import { LOCALSTORAGE } from 'constants/localStorage';
import ContextView from 'container/LogDetailedView/ContextView/ContextView';
import InfraMetrics from 'container/LogDetailedView/InfraMetrics/InfraMetrics';
import Overview from 'container/LogDetailedView/Overview';
import {
	aggregateAttributesResourcesToString,
	getSanitizedLogBody,
	removeEscapeCharacters,
} from 'container/LogDetailedView/utils';
import useInitialQuery from 'container/LogsExplorerContext/useInitialQuery';
import { useOptionsMenu } from 'container/OptionsMenu';
import { FontSize } from 'container/OptionsMenu/types';
import { useCopyLogLink } from 'hooks/logs/useCopyLogLink';
import { useQueryBuilder } from 'hooks/queryBuilder/useQueryBuilder';
import { useIsDarkMode } from 'hooks/useDarkMode';
import { useNotifications } from 'hooks/useNotifications';
import { cloneDeep } from 'lodash-es';
import {
	ArrowDown,
	ArrowUp,
	Braces,
	ChevronDown,
	ChevronUp,
	Compass,
	Copy,
	Filter,
	Histogram,
	Table,
	TextSelect,
	X,
} from '@signozhq/icons';
import { JsonView } from 'periscope/components/JsonView';
import { useAppContext } from 'providers/App/App';
import { ILogBody } from 'types/api/logs/log';
import { Query, TagFilter } from 'types/api/queryBuilder/queryBuilderData';
import { DataSource, StringOperators } from 'types/common/queryBuilder';

import { RESOURCE_KEYS, VIEW_TYPES, VIEWS } from './constants';
import { LogDetailInnerProps, LogDetailProps } from './LogDetail.interfaces';
import LogDetailsHeader from './LogDetailsHeader/LogDetailsHeader';
import { useLogNavigation } from './LogDetailsHeader/useLogNavigation';
import LogHighlights from './LogHighlights/LogHighlights';
import { useIsLogDetailsV2 } from './useIsLogDetailsV2';

import './LogDetails.styles.scss';

/* eslint-disable-next-line sonarjs/cognitive-complexity */
function LogDetailInner({
	log,
	onClose,
	onAddToQuery,
	onClickActionItem,
	selectedTab,
	isListViewPanel = false,
	listViewPanelSelectedFields,
	handleChangeSelectedView,
	logs,
	onNavigateLog,
	onScrollToLog,
	handleOpenInExplorer,
	getContainer,
	onApplyLogFilter,
}: LogDetailInnerProps): JSX.Element {
	const initialContextQuery = useInitialQuery(log);
	const [contextQuery, setContextQuery] = useState<Query | undefined>(
		initialContextQuery,
	);
	const [, copyToClipboard] = useCopyToClipboard();
	const [selectedView, setSelectedView] = useState<VIEWS>(selectedTab);

	const [isFilterVisible, setIsFilterVisible] = useState<boolean>(false);
	const { featureFlags } = useAppContext();
	const isBodyJsonQueryEnabled =
		featureFlags?.find((flag) => flag.name === FeatureKeys.USE_JSON_BODY)
			?.active || false;

	const [filters, setFilters] = useState<TagFilter | null>(null);
	const [isEdit, setIsEdit] = useState<boolean>(false);
	const { stagedQuery } = useQueryBuilder();

	const isLogDetailsV2 = useIsLogDetailsV2();

	// Handle clicks outside to close drawer, except on explicitly ignored regions
	useEffect(() => {
		const handleClickOutside = (e: MouseEvent): void => {
			const target = e.target as HTMLElement;

			// Don't close if clicking on drawer content, overlays, or portal elements
			if (
				target.closest('[data-log-detail-ignore="true"]') ||
				target.closest('.log-detail-drawer') ||
				target.closest('.cm-tooltip-autocomplete') ||
				target.closest('.drawer-popover') ||
				target.closest('.query-status-popover') ||
				target.closest('[data-radix-popper-content-wrapper]')
			) {
				return;
			}

			// Close the drawer for any other outside click
			onClose?.(e as any);
		};

		document.addEventListener('mousedown', handleClickOutside);

		return (): void => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [onClose]);

	const { goToPrev, goToNext, isPrevDisabled, isNextDisabled } =
		useLogNavigation({
			logs,
			activeLogId: log.id,
			onNavigateLog,
			onScrollToLog,
		});

	// Keyboard navigation - handle up/down arrow keys. Only listen in the OVERVIEW
	// tab so we don't hijack arrow keys from the JSON editor / context view.
	useEffect(() => {
		if (selectedView !== VIEW_TYPES.OVERVIEW) {
			return undefined;
		}

		const handleKeyDown = (e: KeyboardEvent): void => {
			if (e.key === 'ArrowUp') {
				e.preventDefault();
				e.stopPropagation();
				goToPrev();
			} else if (e.key === 'ArrowDown') {
				e.preventDefault();
				e.stopPropagation();
				goToNext();
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return (): void => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [selectedView, goToPrev, goToNext]);

	const listQuery = useMemo(() => {
		if (!stagedQuery || stagedQuery.builder.queryData.length < 1) {
			return null;
		}

		return stagedQuery.builder.queryData.find((item) => !item.disabled) || null;
	}, [stagedQuery]);

	const { options } = useOptionsMenu({
		storageKey: LOCALSTORAGE.LOGS_LIST_OPTIONS,
		dataSource: DataSource.LOGS,
		aggregateOperator: listQuery?.aggregateOperator || StringOperators.NOOP,
	});

	const isDarkMode = useIsDarkMode();

	const { notifications } = useNotifications();

	const { onLogCopy } = useCopyLogLink(log?.id);

	const LogJsonData = log ? aggregateAttributesResourcesToString(log) : '';

	const handleModeChange = (value: string): void => {
		setSelectedView(value as VIEWS);
		setIsEdit(false);
		setIsFilterVisible(false);
	};

	const handleFilterVisible = (): void => {
		setIsFilterVisible(!isFilterVisible);
		setIsEdit(!isEdit);
	};

	const drawerCloseHandler = (
		e: React.MouseEvent | React.KeyboardEvent,
	): void => {
		if (onClose) {
			onClose(e);
		}
	};

	const logBody = useMemo(() => {
		if (!isBodyJsonQueryEnabled) {
			return (log?.body as string) ?? '';
		}
		// Feature enabled: body is always a map; message is always a string
		const bodyObj = log?.body as ILogBody;
		if (!bodyObj) {
			return '';
		}
		if (bodyObj.message) {
			return bodyObj.message;
		}
		return JSON.stringify(bodyObj);
	}, [isBodyJsonQueryEnabled, log?.body]);

	const htmlBody = useMemo(
		() => ({
			__html: getSanitizedLogBody(logBody || '', { shouldEscapeHtml: true }),
		}),
		[logBody],
	);

	const handleJSONCopy = (): void => {
		copyToClipboard(LogJsonData);
		notifications.success({
			message: 'Copied to clipboard',
		});
	};

	const handleQueryExpressionChange = useCallback(
		(value: string, queryIndex: number) => {
			// update the query at the given index
			setContextQuery((prev) => {
				if (!prev) {
					return prev;
				}

				return {
					...prev,
					builder: {
						...prev.builder,
						queryData: prev.builder.queryData.map((query, idx) =>
							idx === queryIndex
								? {
										...query,
										filter: {
											...query.filter,
											expression: value,
										},
									}
								: query,
						),
					},
				};
			});
		},
		[],
	);

	const handleRunQuery = (expression: string): void => {
		let updatedContextQuery = cloneDeep(contextQuery);

		if (!updatedContextQuery || !updatedContextQuery.builder) {
			return;
		}

		const newFilters: TagFilter = {
			items: expression ? convertExpressionToFilters(expression) : [],
			op: 'AND',
		};

		updatedContextQuery = {
			...updatedContextQuery,
			builder: {
				...updatedContextQuery?.builder,
				queryData: updatedContextQuery?.builder.queryData.map((queryData) => ({
					...queryData,
					filter: {
						...queryData.filter,
						expression,
					},
					filters: {
						...queryData.filters,
						...newFilters,
						op: queryData.filters?.op ?? 'AND',
					},
				})),
			},
		};

		setContextQuery(updatedContextQuery);

		if (newFilters) {
			setFilters(newFilters);
		}
	};

	const logType = log?.attributes_string?.log_level || LogType.INFO;

	return (
		<Drawer
			width="60%"
			mask={false}
			maskClosable={false}
			getContainer={getContainer}
			title={
				isLogDetailsV2 ? (
					<LogDetailsHeader
						log={log}
						onNavigatePrev={goToPrev}
						onNavigateNext={goToNext}
						isPrevDisabled={isPrevDisabled}
						isNextDisabled={isNextDisabled}
						showOpenInExplorer={!!handleOpenInExplorer}
						onOpenInExplorer={handleOpenInExplorer}
					/>
				) : (
					<div className="log-detail-drawer__title" data-log-detail-ignore="true">
						<div className="log-detail-drawer__title-left">
							<Divider type="vertical" className={cx('log-type-indicator', LogType)} />
							<Typography.Text className="title">Log details</Typography.Text>
						</div>
						<div className="log-detail-drawer__title-right">
							<div className="log-arrows">
								<Tooltip
									title={isPrevDisabled ? '' : 'Move to previous log'}
									placement="top"
									mouseLeaveDelay={0}
								>
									<Button
										variant="outlined"
										color="secondary"
										prefix={<ChevronUp size={14} />}
										className="log-arrow-btn log-arrow-btn-up"
										disabled={isPrevDisabled}
										onClick={goToPrev}
									/>
								</Tooltip>
								<Tooltip
									title={isNextDisabled ? '' : 'Move to next log'}
									placement="top"
									mouseLeaveDelay={0}
								>
									<Button
										variant="outlined"
										color="secondary"
										prefix={<ChevronDown size={14} />}
										className="log-arrow-btn log-arrow-btn-down"
										disabled={isNextDisabled}
										onClick={goToNext}
									/>
								</Tooltip>
							</div>
							{handleOpenInExplorer && (
								<div>
									<Button
										variant="outlined"
										color="secondary"
										prefix={<Compass size={16} />}
										className="open-in-explorer-btn"
										onClick={handleOpenInExplorer}
									>
										Open in Explorer
									</Button>
								</div>
							)}
						</div>
					</div>
				)
			}
			placement="right"
			onClose={drawerCloseHandler}
			open={log !== null}
			style={{
				overscrollBehavior: 'contain',
				background: isDarkMode ? Color.BG_INK_400 : Color.BG_VANILLA_100,
			}}
			className="log-detail-drawer"
			destroyOnClose
			closeIcon={<X size={16} style={{ marginTop: Spacing.MARGIN_1 }} />}
		>
			<div
				className="log-detail-drawer__content"
				data-log-detail-ignore="true"
				data-testid="log-detail-drawer"
			>
				<div className="log-detail-drawer__log">
					{isLogDetailsV2 ? (
						<LogStateIndicator
							severityText={log.severity_text}
							severityNumber={log.severity_number}
							fontSize={options?.fontSize ?? FontSize.MEDIUM}
						/>
					) : (
						<Divider type="vertical" className={cx('log-type-indicator', logType)} />
					)}
					<Tooltip
						title={removeEscapeCharacters(logBody)}
						placement="left"
						mouseLeaveDelay={0}
					>
						<div className="log-body" dangerouslySetInnerHTML={htmlBody} />
					</Tooltip>

					<div className="log-overflow-shadow">&nbsp;</div>
				</div>

				{isLogDetailsV2 && <LogHighlights log={log} />}

				{isLogDetailsV2 && <div className="log-detail-drawer__section-divider" />}

				<div className="tabs-and-search">
					<ToggleGroupSimple
						type="single"
						className="views-tabs"
						onChange={handleModeChange}
						value={selectedView}
						items={[
							{
								value: VIEW_TYPES.OVERVIEW,
								label: (
									<div className="view-title">
										<Table size={14} />
										Overview
									</div>
								),
							},
							// V2's DataViewer has its own Pretty/JSON toggle, so the separate
							// JSON tab is redundant.
							...(isLogDetailsV2
								? []
								: [
										{
											value: VIEW_TYPES.JSON,
											label: (
												<div className="view-title">
													<Braces size={14} />
													JSON
												</div>
											),
										},
									]),
							{
								value: VIEW_TYPES.CONTEXT,
								label: (
									<div className="view-title">
										<TextSelect size={14} />
										Context
									</div>
								),
							},
							{
								value: VIEW_TYPES.INFRAMETRICS,
								label: (
									<div className="view-title">
										<Histogram size="md" />
										Metrics
									</div>
								),
							},
						]}
					/>

					<div className="log-detail-drawer__actions">
						{selectedView === VIEW_TYPES.CONTEXT && (
							<Tooltip
								title="Show Filters"
								placement="topLeft"
								aria-label="Show Filters"
								mouseLeaveDelay={0}
							>
								<Button
									variant="link"
									color="secondary"
									size="sm"
									prefix={<Filter size="lg" />}
									onClick={handleFilterVisible}
								/>
							</Tooltip>
						)}

						{/* V2 moves copy actions into the header ⋯ menu */}
						{!isLogDetailsV2 && (
							<Tooltip
								title={selectedView === VIEW_TYPES.JSON ? 'Copy JSON' : 'Copy Log Link'}
								placement="topLeft"
								aria-label={
									selectedView === VIEW_TYPES.JSON ? 'Copy JSON' : 'Copy Log Link'
								}
								mouseLeaveDelay={0}
							>
								<Button
									variant="link"
									color="secondary"
									size="sm"
									prefix={<Copy size={12} />}
									onClick={selectedView === VIEW_TYPES.JSON ? handleJSONCopy : onLogCopy}
								/>
							</Tooltip>
						)}
					</div>
				</div>
				{isFilterVisible && contextQuery?.builder.queryData[0] && (
					<div className="log-detail-drawer-query-container">
						<QuerySearch
							onChange={(value): void => handleQueryExpressionChange(value, 0)}
							dataSource={DataSource.LOGS}
							queryData={contextQuery?.builder.queryData[0]}
							onRun={handleRunQuery}
						/>
					</div>
				)}

				{selectedView === VIEW_TYPES.OVERVIEW && (
					<Overview
						logData={log}
						onAddToQuery={onAddToQuery}
						onClickActionItem={onClickActionItem}
						isListViewPanel={isListViewPanel}
						selectedOptions={options}
						listViewPanelSelectedFields={listViewPanelSelectedFields}
						handleChangeSelectedView={handleChangeSelectedView}
						onApplyLogFilter={onApplyLogFilter}
					/>
				)}
				{!isLogDetailsV2 && selectedView === VIEW_TYPES.JSON && (
					<JsonView data={LogJsonData} height="68vh" />
				)}

				{selectedView === VIEW_TYPES.CONTEXT && (
					<ContextView
						log={log}
						filters={filters}
						contextQuery={contextQuery}
						isEdit={isEdit}
					/>
				)}
				{selectedView === VIEW_TYPES.INFRAMETRICS && (
					<InfraMetrics
						clusterName={log.resources_string?.[RESOURCE_KEYS.CLUSTER_NAME] || ''}
						podName={log.resources_string?.[RESOURCE_KEYS.POD_NAME] || ''}
						nodeName={log.resources_string?.[RESOURCE_KEYS.NODE_NAME] || ''}
						hostName={log.resources_string?.[RESOURCE_KEYS.HOST_NAME] || ''}
						timestamp={log.timestamp.toString()}
						dataSource={DataSource.LOGS}
					/>
				)}

				{selectedView === VIEW_TYPES.OVERVIEW && (
					<div className="log-detail-drawer__footer-hint">
						<div className="log-detail-drawer__footer-hint-content">
							<Typography.Text
								color="muted"
								className="log-detail-drawer__footer-hint-text"
							>
								Use
							</Typography.Text>
							<ArrowUp size={14} className="log-detail-drawer__footer-hint-icon" />
							<span>/</span>
							<ArrowDown size={14} className="log-detail-drawer__footer-hint-icon" />
							<Typography.Text
								color="muted"
								className="log-detail-drawer__footer-hint-text"
							>
								to view previous/next log
							</Typography.Text>
						</div>
					</div>
				)}
			</div>
		</Drawer>
	);
}

function LogDetail(props: LogDetailProps): JSX.Element {
	const { log } = props;
	if (!log) {
		return <></>;
	}

	return <LogDetailInner {...(props as LogDetailInnerProps)} />;
}

export default LogDetail;
